import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { listLeads, setLeadStatus } from "@/lib/backend";
import { formatApiErrorDetail } from "@/lib/api";
import { SignOut, ArrowSquareOut, Phone, EnvelopeSimple } from "@phosphor-icons/react";

const STATUS_OPTIONS = ["new", "contacted", "quoted", "won", "lost"];
const TRACK_COLORS = {
  build: "text-amber-400",
  grow: "text-primary",
  both: "text-emerald-400",
};

// Same figures the old /admin/stats endpoint returned, computed client-side.
function computeStats(leads) {
  return {
    total: leads.length,
    new: leads.filter((l) => l.status === "new").length,
    build: leads.filter((l) => l.track === "build").length,
    grow: leads.filter((l) => l.track === "grow").length,
    both: leads.filter((l) => l.track === "both").length,
  };
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (user && user.role === "admin") {
      let active = true;
      listLeads()
        .then((rows) => {
          if (!active) return;
          setLeads(rows);
          setStats(computeStats(rows));
        })
        .catch((err) => { if (active) setError(formatApiErrorDetail(err.response?.data?.detail)); })
        .finally(() => { if (active) setLoading(false); });
      return () => { active = false; };
    }
  }, [user]);

  if (user === null) {
    return <div className="min-h-screen grid place-items-center font-mono-tech text-muted-foreground">Loading…</div>;
  }
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    setSaving(id);
    setError("");
    try {
      await setLeadStatus(id, status);
      const next = leads.map((lead) => lead.id === id ? { ...lead, status } : lead);
      setLeads(next);
      setStats(computeStats(next));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(null); }
  };

  const signOut = async () => {
    try { await logout(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const filtered = filter === "all" ? leads : leads.filter((l) => l.track === filter);

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/90 backdrop-blur-md z-10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-8 h-8 bg-primary text-primary-foreground font-display">M</span>
            <span className="font-mono-tech text-xs tracking-widest uppercase">Lead Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground hover:text-primary flex items-center gap-1.5">
              View site <ArrowSquareOut size={13} />
            </Link>
            <button onClick={signOut} data-testid="admin-logout" className="font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground hover:text-primary flex items-center gap-1.5">
              <SignOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display uppercase tracking-tight text-4xl mb-8">Leads</h1>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border mb-10">
            {[
              { label: "Total", value: stats.total },
              { label: "New", value: stats.new },
              { label: "Build", value: stats.build },
              { label: "Grow", value: stats.grow },
              { label: "Both", value: stats.both },
            ].map((s) => (
              <div key={s.label} className="bg-card p-6" data-testid={`admin-stat-${s.label.toLowerCase()}`}>
                <div className="font-display text-4xl text-foreground">{s.value}</div>
                <div className="font-mono-tech text-[10px] tracking-widest uppercase text-muted-foreground mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["all", "build", "grow", "both"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`admin-filter-${f}`}
              className={`font-mono-tech text-[11px] tracking-widest uppercase px-4 h-9 border transition-colors ${
                filter === f ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* List */}
        {error && <p role="alert" className="text-destructive font-mono-tech mb-4">{error}</p>}
        {loading ? (
          <p className="font-mono-tech text-muted-foreground">Loading leads…</p>
        ) : error && !stats ? null : filtered.length === 0 ? (
          <div className="border border-border p-12 text-center" data-testid="admin-empty">
            <p className="font-mono-tech text-muted-foreground tracking-widest uppercase text-sm">No leads yet</p>
            <p className="text-muted-foreground mt-2">New requests from the site will appear here.</p>
          </div>
        ) : (
          <div className="space-y-px bg-border border border-border" data-testid="admin-leads-list">
            {filtered.map((lead) => (
              <div key={lead.id} className="bg-card p-6 grid md:grid-cols-12 gap-4 items-start" data-testid={`admin-lead-${lead.id}`}>
                <div className="md:col-span-3 min-w-0">
                  <p className="font-display uppercase tracking-tight text-xl break-words">{lead.name}</p>
                  <div className="mt-2 space-y-1">
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
                      <EnvelopeSimple size={14} /> {lead.email}
                    </a>
                    {lead.phone && (
                      <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
                        <Phone size={14} /> {lead.phone}
                      </a>
                    )}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <p className={`font-mono-tech text-xs tracking-widest uppercase ${TRACK_COLORS[lead.track] || "text-foreground"}`}>{lead.track}</p>
                  {lead.project_type && <p className="text-sm text-muted-foreground mt-1">{lead.project_type}</p>}
                  {(lead.estimate_low || lead.estimate_high) && (
                    <p className="text-sm text-primary font-mono-tech mt-1">
                      ${lead.estimate_low?.toLocaleString()} – ${lead.estimate_high?.toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="md:col-span-4">
                  {lead.message && <p className="text-sm text-muted-foreground leading-relaxed">{lead.message}</p>}
                  <p className="font-mono-tech text-[10px] tracking-widest uppercase text-muted-foreground/60 mt-2">
                    {new Date(lead.created_at).toLocaleString("en-CA")} · {lead.source}
                  </p>
                </div>
                <div className="md:col-span-3 md:text-right">
                  <select
                    disabled={saving !== null}
                    value={lead.status}
                    onChange={(e) => updateStatus(lead.id, e.target.value)}
                    data-testid={`admin-status-${lead.id}`}
                    className="bg-secondary/60 border border-border focus:border-primary outline-none px-3 h-10 font-mono-tech text-xs uppercase tracking-widest"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s} className="bg-background">{s}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
